import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Table,
  Badge,
  Alert,
  Spinner,
  Button,
  Form,
  Modal,
  InputGroup,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaTrash, FaUserCog, FaEye, FaEyeSlash } from "react-icons/fa";
import { useAuth } from "./auth/AuthContext";

const candidateBases = Array.from(
  new Set(
    [process.env.REACT_APP_API_BASE_URL, "https://api.quvikme.com"].filter(
      Boolean
    )
  )
);

const SubAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subAdmins, setSubAdmins] = useState([]);
  const [subSubAdmins, setSubSubAdmins] = useState([]); // New state for sub-sub admins
  const [showModal, setShowModal] = useState(false);
  const [showSubSubModal, setShowSubSubModal] = useState(false); // New state for sub-sub admin modal
  const [subSubAdminsModal, setSubSubAdminsModal] = useState(false); // New state for sub-sub admins detail modal
  const [selectedSubAdmin, setSelectedSubAdmin] = useState(null); // Track which sub-admin we're creating sub-sub admin for
  const [form, setForm] = useState({
    name: "",
    email: "",
    areaName: "",
    pincode: "",
    password: "",
  });
  const [subSubForm, setSubSubForm] = useState({
    name: "",
    email: "",
    societyName: "",
    password: "",
    customId: "",
  }); // Form for sub-sub admin
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [pwdTarget, setPwdTarget] = useState(null);
  const [pwdValue, setPwdValue] = useState("");
  const [pwdSaving, setPwdSaving] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    areaName: "",
    pincode: "",
    status: "active",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false); // For create modal
  const [showEditPassword, setShowEditPassword] = useState(false); // For edit modal
  const navigate = useNavigate();
  const { role, societyId, isAdmin, isSubadmin, id } = useAuth(); // Added id to get current user ID

  useEffect(() => {
    fetchSubAdmins();
    fetchSubSubAdmins(); // Fetch sub-sub admins as well
  }, []);

  const fetchSubAdmins = async () => {
    try {
      setLoading(true);
      let data = null;
      let lastErr = null;
      for (const base of candidateBases) {
        try {
          // Get admin token for authentication
          const adminToken =
            localStorage.getItem("adminToken") ||
            sessionStorage.getItem("adminToken");
          const headers = {
            "Content-Type": "application/json",
          };
          if (adminToken) {
            headers["Authorization"] = `Bearer ${adminToken}`;
          }

          const res = await fetch(`${base}/api/admins/subadmins`, { headers });
          if (!res.ok) {
            lastErr = new Error(await res.text());
            continue;
          }
          data = await res.json();
          break;
        } catch (e2) {
          lastErr = e2;
          continue;
        }
      }
      if (!data) throw lastErr || new Error("Failed to load subadmins");
      setSubAdmins(Array.isArray(data.subadmins) ? data.subadmins : []);
      setLoading(false);
    } catch (e) {
      setError("Failed to load sub-admins");
      setLoading(false);
    }
  };

  const fetchSubSubAdmins = async () => {
    try {
      let data = null;
      let lastErr = null;
      for (const base of candidateBases) {
        try {
          // Get admin token for authentication
          const adminToken =
            localStorage.getItem("adminToken") ||
            sessionStorage.getItem("adminToken");
          const headers = {
            "Content-Type": "application/json",
          };
          if (adminToken) {
            headers["Authorization"] = `Bearer ${adminToken}`;
          }

          const res = await fetch(`${base}/api/admins/subsubadmins`, {
            headers,
          });
          if (!res.ok) {
            lastErr = new Error(await res.text());
            continue;
          }
          data = await res.json();
          break;
        } catch (e2) {
          lastErr = e2;
          continue;
        }
      }
      if (!data) throw lastErr || new Error("Failed to load sub-sub admins");
      setSubSubAdmins(
        Array.isArray(data.subsubadmins) ? data.subsubadmins : []
      );
    } catch (e) {
      console.error("Failed to load sub-sub admins:", e);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError(null);
    if (
      !form.name ||
      !form.email ||
      !form.areaName ||
      !form.pincode ||
      !form.password
    )
      return;
    try {
      let created = null;
      let lastErr = null;
      for (const base of candidateBases) {
        try {
          // Get admin token for authentication
          const adminToken =
            localStorage.getItem("adminToken") ||
            sessionStorage.getItem("adminToken");
          const headers = {
            "Content-Type": "application/json",
          };
          if (adminToken) {
            headers["Authorization"] = `Bearer ${adminToken}`;
          }

          const res = await fetch(`${base}/api/admins/subadmins`, {
            method: "POST",
            headers,
            body: JSON.stringify({ ...form }),
          });

          if (!res.ok) {
            const errorText = await res.text();
            let errorMessage = "Failed to create sub-admin";

            // Try to parse the error response
            try {
              const errorResponse = JSON.parse(errorText);
              if (errorResponse.error) {
                errorMessage = errorResponse.error;
              }
            } catch (parseError) {
              // If parsing fails, use the raw error text
              if (errorText) {
                errorMessage = errorText;
              }
            }

            lastErr = new Error(errorMessage);
            continue;
          }

          created = await res.json();
          break;
        } catch (e2) {
          lastErr = e2;
          continue;
        }
      }
      if (!created) throw lastErr || new Error("Create failed");
      setSubAdmins((prev) => [created, ...prev]);
      setForm({ name: "", email: "", areaName: "", pincode: "", password: "" });
      setShowModal(false);
    } catch (e) {
      setError(e.message || "Failed to create sub-admin");
    }
  };

  // New function to handle sub-sub admin creation
  const handleCreateSubSubAdmin = async (e) => {
    e.preventDefault();
    setError(null);
    if (
      !subSubForm.name ||
      !subSubForm.email ||
      !subSubForm.societyName ||
      !subSubForm.password
    )
      return;

    // If selectedSubAdmin is not set (admin creating), socId is required
    if (!selectedSubAdmin && !subSubForm.socId) return;

    try {
      let created = null;
      let lastErr = null;
      for (const base of candidateBases) {
        try {
          const payload = {
            ...subSubForm,
          };

          // If sub-admin is creating, auto-assign their ID
          // If admin is creating, use the selected socId
          if (selectedSubAdmin) {
            payload.socId = selectedSubAdmin.id;
          }

          // Only include customId if it's not empty
          if (!subSubForm.customId) {
            delete payload.customId;
          }

          // Get admin token for authentication
          const adminToken =
            localStorage.getItem("adminToken") ||
            sessionStorage.getItem("adminToken");
          const headers = {
            "Content-Type": "application/json",
          };
          if (adminToken) {
            headers["Authorization"] = `Bearer ${adminToken}`;
          }

          const res = await fetch(`${base}/api/admins/subsubadmins`, {
            method: "POST",
            headers,
            body: JSON.stringify(payload),
          });

          if (!res.ok) {
            const errorText = await res.text();
            let errorMessage = "Failed to create sub-sub admin";

            // Try to parse the error response
            try {
              const errorResponse = JSON.parse(errorText);
              if (errorResponse.error) {
                errorMessage = errorResponse.error;
              }
            } catch (parseError) {
              // If parsing fails, use the raw error text
              if (errorText) {
                errorMessage = errorText;
              }
            }

            lastErr = new Error(errorMessage);
            continue;
          }

          created = await res.json();
          break;
        } catch (e2) {
          lastErr = e2;
          continue;
        }
      }
      if (!created) throw lastErr || new Error("Create failed");
      setSubSubAdmins((prev) => [created, ...prev]);
      setSubSubForm({
        name: "",
        email: "",
        societyName: "",
        password: "",
        customId: "",
        socId: "",
      });
      setShowSubSubModal(false);
      setSelectedSubAdmin(null);
    } catch (e) {
      setError(e.message || "Failed to create sub-sub admin");
    }
  };

  const handleDelete = async (id) => {
    setError(null); // Clear any previous errors
    try {
      let ok = false;
      let lastErr = null;
      for (const base of candidateBases) {
        try {
          // Get admin token for authentication
          const adminToken =
            localStorage.getItem("adminToken") ||
            sessionStorage.getItem("adminToken");
          const headers = {};
          if (adminToken) {
            headers["Authorization"] = `Bearer ${adminToken}`;
          }

          const res = await fetch(`${base}/api/admins/subadmins/${id}`, {
            method: "DELETE",
            headers,
          });

          if (!res.ok) {
            const errorText = await res.text();
            let errorMessage = "Failed to delete sub-admin";

            // Try to parse the error response
            try {
              const errorResponse = JSON.parse(errorText);
              if (errorResponse.error) {
                errorMessage = errorResponse.error;
              }
            } catch (parseError) {
              // If parsing fails, use the raw error text
              if (errorText) {
                errorMessage = errorText;
              }
            }

            lastErr = new Error(errorMessage);
            continue;
          }

          ok = true;
          break;
        } catch (e2) {
          lastErr = e2;
          continue;
        }
      }
      if (!ok) throw lastErr || new Error("Delete failed");
      setSubAdmins((prev) => prev.filter((sa) => sa.id !== id));
      setDeleteConfirm(null);
    } catch (e) {
      setError(e.message || "Failed to delete sub-admin");
    }
  };

  const handleSetPassword = async () => {
    if (!pwdTarget || !pwdValue || pwdValue.length < 6) return;
    setError(null); // Clear any previous errors
    setPwdSaving(true);
    try {
      let ok = false;
      let lastErr = null;
      for (const base of candidateBases) {
        try {
          // Get admin token for authentication
          const adminToken =
            localStorage.getItem("adminToken") ||
            sessionStorage.getItem("adminToken");
          const headers = {
            "Content-Type": "application/json",
          };
          if (adminToken) {
            headers["Authorization"] = `Bearer ${adminToken}`;
          }

          const res = await fetch(
            `${base}/api/admins/subadmins/${pwdTarget.id}/password`,
            {
              method: "PUT",
              headers,
              body: JSON.stringify({ password: pwdValue }),
            }
          );

          if (!res.ok) {
            const errorText = await res.text();
            let errorMessage = "Failed to update password";

            // Try to parse the error response
            try {
              const errorResponse = JSON.parse(errorText);
              if (errorResponse.error) {
                errorMessage = errorResponse.error;
              }
            } catch (parseError) {
              // If parsing fails, use the raw error text
              if (errorText) {
                errorMessage = errorText;
              }
            }

            lastErr = new Error(errorMessage);
            continue;
          }

          ok = true;
          break;
        } catch (e2) {
          lastErr = e2;
          continue;
        }
      }
      if (!ok) throw lastErr || new Error("Update failed");
      setPwdTarget(null);
      setPwdValue("");
    } catch (e) {
      setError(e.message || "Failed to update password");
    } finally {
      setPwdSaving(false);
    }
  };

  const handleViewAs = (sa) => {
    try {
      sessionStorage.setItem("QV_ROLE", "subadmin");
      if (sa.areaName)
        sessionStorage.setItem("QV_SOCIETY_ID", String(sa.areaName));
      if (sa.name) sessionStorage.setItem("QV_SUBADMIN_NAME", String(sa.name));
      // Open dashboard in a new tab as the subadmin
      window.open("/", "_blank");
    } catch { }
  };

  const openEdit = (sa) => {
    setEditTarget(sa);
    setEditForm({
      name: sa.name || "",
      email: sa.email || "",
      areaName: sa.areaName || "",
      pincode: sa.pincode || "",
      status: (sa.status || "active").toString().toLowerCase(),
      password: "",
    });
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    setError(null); // Clear any previous errors
    const payload = {
      name: editForm.name,
      email: editForm.email,
      areaName: editForm.areaName,
      pincode: editForm.pincode,
      status: editForm.status,
    };
    if (editForm.password && editForm.password.length >= 6)
      payload.password = editForm.password;
    try {
      let updated = null;
      let lastErr = null;
      for (const base of candidateBases) {
        try {
          // Get admin token for authentication
          const adminToken =
            localStorage.getItem("adminToken") ||
            sessionStorage.getItem("adminToken");
          const headers = {
            "Content-Type": "application/json",
          };
          if (adminToken) {
            headers["Authorization"] = `Bearer ${adminToken}`;
          }

          const res = await fetch(
            `${base}/api/admins/subadmins/${editTarget.id}`,
            {
              method: "PUT",
              headers,
              body: JSON.stringify(payload),
            }
          );

          if (!res.ok) {
            const errorText = await res.text();
            let errorMessage = "Failed to update sub-admin";

            // Try to parse the error response
            try {
              const errorResponse = JSON.parse(errorText);
              if (errorResponse.error) {
                errorMessage = errorResponse.error;
              }
            } catch (parseError) {
              // If parsing fails, use the raw error text
              if (errorText) {
                errorMessage = errorText;
              }
            }

            lastErr = new Error(errorMessage);
            continue;
          }

          updated = await res.json();
          break;
        } catch (e2) {
          lastErr = e2;
          continue;
        }
      }
      if (!updated) throw lastErr || new Error("Update failed");
      setSubAdmins((prev) =>
        prev.map((sa) => (sa.id === updated.id ? updated : sa))
      );
      setEditTarget(null);
    } catch (e) {
      setError(e.message || "Failed to update sub-admin");
    }
  };

  // Function to open sub-sub admin creation modal
  const openCreateSubSubAdmin = (subAdmin) => {
    setSelectedSubAdmin(subAdmin);
    setSubSubForm({
      name: "",
      email: "",
      societyName: `${subAdmin.areaName} Society`,
      password: "",
      customId: "",
    });
    setShowSubSubModal(true);
  };

  // Function to get sub-sub admins for a specific sub-admin
  const getSubSubAdminsForSubAdmin = (subAdminId) => {
    return subSubAdmins.filter((ssa) => ssa.socId === subAdminId);
  };

  const statusBadge = (status) => (
    <Badge
      bg={String(status).toLowerCase() === "active" ? "success" : "secondary"}
      className="p-2"
    >
      {String(status || "active").toUpperCase()}
    </Badge>
  );

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "40vh" }}
      >
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    // Clear the top-level error after 5 seconds
    setTimeout(() => {
      setError(null);
    }, 5000);

    return (
      <Alert variant="danger" className="mx-3">
        <Alert.Heading>Error</Alert.Heading>
        <p>{error}</p>
        <Button variant="outline-danger" onClick={() => setError(null)}>
          Dismiss
        </Button>
      </Alert>
    );
  }

  return (
    <Container fluid className="px-3 px-md-4 py-3">
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2 mb-4">
        <h2 className="mb-0 fw-bold">Sub-Admins</h2>
        <Button variant="primary" size="sm" onClick={() => setShowModal(true)}>
          <FaPlus className="me-2" /> Add Sub-Admin
        </Button>
      </div>

      <Card className="shadow-sm border-0">
        <Card.Header className="bg-light border-0 py-3">
          <h5 className="mb-0 fw-bold">Manage Sub-Admins</h5>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="align-middle text-nowrap mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Area</th>
                <th>Pincode</th>
                <th>Sub-Sub Admins</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subAdmins.map((sa) => {
                const associatedSubSubAdmins = getSubSubAdminsForSubAdmin(
                  sa.id
                );
                return (
                  <tr key={sa.id}>
                    <td>{sa.name}</td>
                    <td>{sa.email}</td>
                    <td>{sa.areaName}</td>
                    <td>{sa.pincode}</td>
                    <td>
                      {associatedSubSubAdmins.length > 0 ? (
                        <Button
                          variant="link"
                          className="p-0"
                          onClick={() => {
                            setSelectedSubAdmin(sa);
                            setSubSubAdminsModal(true);
                          }}
                        >
                          {associatedSubSubAdmins.length} Sub-Sub Admin(s)
                        </Button>
                      ) : (
                        <span className="text-muted">0</span>
                      )}
                    </td>
                    <td>{statusBadge(sa.status)}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => openEdit(sa)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-info"
                          onClick={() => openCreateSubSubAdmin(sa)}
                        >
                          <FaPlus className="me-1" /> Add Sub-Sub Admin
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => setDeleteConfirm(sa)}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Create Sub-Admin Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Form onSubmit={handleCreate}>
          <Modal.Header closeButton>
            <Modal.Title>Create Sub-Admin</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && (
              <Alert variant="danger" className="mb-3">
                <Alert.Heading>Error</Alert.Heading>
                <p>{error}</p>
              </Alert>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Area Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter area name"
                value={form.areaName}
                onChange={(e) => setForm({ ...form, areaName: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Pincode</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter pincode"
                value={form.pincode}
                onChange={(e) => setForm({ ...form, pincode: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  value={form.password}
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                  required
                  minLength="6"
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </Button>
              </InputGroup>
              <Form.Text className="text-muted">
                Password must be at least 6 characters long.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                setError(null); // Clear error when closing modal
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Create
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Create Sub-Sub Admin Modal */}
      <Modal
        show={showSubSubModal}
        onHide={() => {
          setShowSubSubModal(false);
          setSelectedSubAdmin(null);
          setError(null); // Clear error when closing modal
        }}
      >
        <Form onSubmit={handleCreateSubSubAdmin}>
          <Modal.Header closeButton>
            <Modal.Title>
              {selectedSubAdmin
                ? `Create Sub-Sub Admin for ${selectedSubAdmin.areaName}`
                : "Create Sub-Sub Admin"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && (
              <Alert variant="danger" className="mb-3">
                <Alert.Heading>Error</Alert.Heading>
                <p>{error}</p>
              </Alert>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name"
                value={subSubForm.name}
                onChange={(e) =>
                  setSubSubForm({ ...subSubForm, name: e.target.value })
                }
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                value={subSubForm.email}
                onChange={(e) =>
                  setSubSubForm({ ...subSubForm, email: e.target.value })
                }
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Custom ID (Optional)</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter custom ID (e.g., 1, 2, 3)"
                value={subSubForm.customId}
                onChange={(e) =>
                  setSubSubForm({ ...subSubForm, customId: e.target.value })
                }
              />
            </Form.Group>
            {!selectedSubAdmin && (
              <Form.Group className="mb-3">
                <Form.Label>Parent Sub-Admin</Form.Label>
                <Form.Select
                  value={subSubForm.socId || ""}
                  onChange={(e) =>
                    setSubSubForm({ ...subSubForm, socId: e.target.value })
                  }
                  required
                >
                  <option value="">Select a sub-admin</option>
                  {subAdmins.map((subAdmin) => (
                    <option key={subAdmin.id} value={subAdmin.id}>
                      {subAdmin.name} ({subAdmin.areaName})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Society Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter society name"
                value={subSubForm.societyName}
                onChange={(e) =>
                  setSubSubForm({ ...subSubForm, societyName: e.target.value })
                }
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type="password"
                  placeholder="Enter password"
                  value={subSubForm.password}
                  onChange={(e) =>
                    setSubSubForm({ ...subSubForm, password: e.target.value })
                  }
                  required
                  minLength="6"
                />
              </InputGroup>
              <Form.Text className="text-muted">
                Password must be at least 6 characters long.
              </Form.Text>
            </Form.Group>
            {selectedSubAdmin && (
              <Alert variant="info">
                <small>
                  This sub-sub admin will be associated with{" "}
                  {selectedSubAdmin.name} ({selectedSubAdmin.areaName})
                </small>
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowSubSubModal(false);
                setSelectedSubAdmin(null);
                setError(null); // Clear error when closing modal
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Create Sub-Sub Admin
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Sub-Admin Modal */}
      {editTarget && (
        <Modal
          show={!!editTarget}
          onHide={() => {
            setEditTarget(null);
            setError(null); // Clear error when closing modal
          }}
        >
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              saveEdit();
            }}
          >
            <Modal.Header closeButton>
              <Modal.Title>Edit Sub-Admin</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {error && (
                <Alert variant="danger" className="mb-3">
                  <Alert.Heading>Error</Alert.Heading>
                  <p>{error}</p>
                </Alert>
              )}
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter name"
                  value={editForm.name}
                  onChange={(e) =>
                    setEditForm({ ...editForm, name: e.target.value })
                  }
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter email"
                  value={editForm.email}
                  onChange={(e) =>
                    setEditForm({ ...editForm, email: e.target.value })
                  }
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Area Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter area name"
                  value={editForm.areaName}
                  onChange={(e) =>
                    setEditForm({ ...editForm, areaName: e.target.value })
                  }
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Pincode</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter pincode"
                  value={editForm.pincode}
                  onChange={(e) =>
                    setEditForm({ ...editForm, pincode: e.target.value })
                  }
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={editForm.status}
                  onChange={(e) =>
                    setEditForm({ ...editForm, status: e.target.value })
                  }
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Password (leave blank to keep current)</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="password"
                    placeholder="Enter new password"
                    value={editForm.password}
                    onChange={(e) =>
                      setEditForm({ ...editForm, password: e.target.value })
                    }
                    minLength="6"
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowEditPassword(!showEditPassword)}
                  >
                    {showEditPassword ? <FaEyeSlash /> : <FaEye />}
                  </Button>
                </InputGroup>
                <Form.Text className="text-muted">
                  Password must be at least 6 characters long.
                </Form.Text>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => {
                  setEditTarget(null);
                  setError(null); // Clear error when closing modal
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Save Changes
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={!!deleteConfirm} onHide={() => setDeleteConfirm(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete sub-admin{" "}
          <strong>{deleteConfirm?.name}</strong>? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => handleDelete(deleteConfirm?.id)}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Password Change Modal */}
      <Modal
        show={!!pwdTarget}
        onHide={() => {
          setPwdTarget(null);
          setPwdValue("");
          setError(null); // Clear error when closing modal
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Change Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              <Alert.Heading>Error</Alert.Heading>
              <p>{error}</p>
            </Alert>
          )}
          <Form.Group>
            <Form.Label>New Password for {pwdTarget?.name}</Form.Label>
            <Form.Control
              type="password"
              placeholder="Enter new password"
              value={pwdValue}
              onChange={(e) => setPwdValue(e.target.value)}
              minLength="6"
              disabled={pwdSaving}
            />
            <Form.Text className="text-muted">
              Password must be at least 6 characters long.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setPwdTarget(null);
              setPwdValue("");
              setError(null); // Clear error when closing modal
            }}
            disabled={pwdSaving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSetPassword}
            disabled={pwdSaving || !pwdValue || pwdValue.length < 6}
          >
            {pwdSaving ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Sub-Sub Admins Detail Modal */}
      <Modal
        show={subSubAdminsModal}
        onHide={() => {
          setSubSubAdminsModal(false);
          setSelectedSubAdmin(null);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Sub-Sub Admins for {selectedSubAdmin?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSubAdmin && (
            <div>
              <h6>Area: {selectedSubAdmin.areaName}</h6>
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Society Name</th>
                  </tr>
                </thead>
                <tbody>
                  {getSubSubAdminsForSubAdmin(selectedSubAdmin.id).map(
                    (ssa, index) => (
                      <tr key={ssa.id}>
                        <td>{ssa.customId || index + 1}</td>
                        <td>{ssa.name}</td>
                        <td>{ssa.email}</td>
                        <td>{ssa.societyName}</td>
                      </tr>
                    )
                  )}
                </tbody>
              </Table>
              {getSubSubAdminsForSubAdmin(selectedSubAdmin.id).length === 0 && (
                <p className="text-muted text-center">
                  No sub-sub admins found
                </p>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setSubSubAdminsModal(false);
              setSelectedSubAdmin(null);
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SubAdmin;
